import {
  ClipboardList,
  PenSquare,
  Rocket,
  ChartNoAxesCombined,
  Settings,
  MessageCircle,
  Sparkles,
  ScrollText,
  MoreHorizontal,
} from 'lucide-react';
import React, { ComponentType } from 'react';

export type SidebarItemType = 'link' | 'button' | 'divider' | 'custom';

export interface SidebarItemConfig {
  id: string;
  renderType?: ('mini' | 'full' | 'extension')[];
  type: SidebarItemType;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  to?: string; // For link type
  onClick?: () => void; // For button type
  testId?: string;
  tooltipLabel?: string;
  badge?: string;
  customRender?: (props: { isCollapsed: boolean }) => React.ReactNode; // For custom type
  isHidden?: (context?: unknown) => boolean; // Dynamic visibility
  exact?: boolean; // If true, route must match exactly (no child routes)
  /** Key for hover content in mini view; parent provides content via hoverContentMap. */
  hoverContentKey?: string;
  /** Optional title for hover popover; falls back to label when not set. */
  hoverTitle?: string;
}

export interface SidebarSectionConfig {
  id: string;
  title?: string; // Optional section title
  items: SidebarItemConfig[];
  position: 'top' | 'middle' | 'bottom';
  className?: string;
}

/**
 * Centralized sidebar configuration
 * Defines all navigation items and their organization
 */
export const SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  // Top section - Quick actions
  {
    id: 'quick-actions',
    position: 'top',
    items: [
      {
        renderType: ['mini', 'full', 'extension'],
        id: 'new-chat',
        type: 'button',
        label: 'New chat',
        icon: PenSquare,
        testId: 'new-chat-nav-item',
        tooltipLabel: 'Start a new chat',
      },
      {
        renderType: ['mini', 'full', 'extension'],
        id: 'quickstart',
        type: 'link',
        label: 'Quickstart',
        icon: Rocket,
        to: '/quick-start',
        testId: 'quickstart-nav-item',
        hoverContentKey: 'quickstart',
        hoverTitle: '', // No popover title; intro line is inside QuickstartHoverContent
      },
      {
        renderType: ['mini', 'full', 'extension'],
        id: 'task',
        type: 'link',
        label: 'Tasks',
        icon: ClipboardList,
        to: '/tasks',
        testId: 'task-nav-item',
        hoverContentKey: 'task',
      },
      {
        renderType: ['mini', 'full', 'extension'],
        id: 'dashboards',
        type: 'link',
        label: 'Dashboards',
        icon: ChartNoAxesCombined,
        to: '/dashboard',
        testId: 'dashboards-nav-item',
        hoverContentKey: 'dashboards',
      },
      {
        renderType: ['mini', 'full', 'extension'],
        id: 'spotlights',
        type: 'link',
        label: 'Spotlights',
        icon: Sparkles,
        to: '/spotlights',
        testId: 'spotlights-nav-item',
        tooltipLabel: 'Spotlights',
        hoverContentKey: 'spotlights',
      },
      {
        renderType: ['mini', 'full', 'extension'],
        id: 'skills',
        type: 'link',
        label: 'Skills',
        icon: ScrollText,
        to: '/skills',
        testId: 'skills-nav-item',
        tooltipLabel: 'Skills',
      },
    ],
  },

  // Middle section - User & Tools
  {
    id: 'conversations',
    position: 'top',
    items: [
      {
        renderType: ['mini', 'extension'],
        id: 'recent-chats',
        type: 'link',
        label: 'Recent chats',
        testId: 'recent-chats-nav-item',
        icon: MessageCircle,
        to: '/c',
        exact: true, // Only active on /c, not /c/$conversationId
        tooltipLabel: 'Recent chats',
      },
    ],
  },
  // Middle section - User & Tools
  {
    id: 'user-tools',
    position: 'middle',
    items: [],
  },
  // Bottom - Settings (standalone)
  {
    id: 'settings',
    position: 'bottom',
    items: [
      {
        id: 'settings',
        renderType: ['mini', 'full', 'extension'],
        type: 'link',
        label: 'Settings',
        icon: Settings,
        to: '/settings/preferences',
        tooltipLabel: 'Open settings',
        testId: 'settings-nav-item',
        hoverContentKey: 'settings',
      },
    ],
  },
  // Bottom - More (single item; opens popover with Chat with docs, Feature request, Contact support)
  {
    id: 'more',
    position: 'bottom',
    items: [
      {
        id: 'more',
        renderType: ['mini', 'full', 'extension'],
        type: 'custom',
        label: 'More',
        icon: MoreHorizontal,
        tooltipLabel: 'More options',
        testId: 'more-nav-item',
      },
    ],
  },
];

/**
 * Detect if we're running in a browser extension context
 */
const isExtension = (): boolean => {
  return typeof chrome !== 'undefined' && chrome.runtime && !!chrome.runtime.id;
};

/**
 * Get sections by position, filtering items based on renderType
 * Automatically detects extension context and uses 'extension' render type if applicable
 * Items without renderType default to ['mini']
 */
export const getSectionsByPosition = (
  position: 'top' | 'middle' | 'bottom',
  renderType?: 'mini' | 'full' | 'extension',
) => {
  // Auto-detect extension context if renderType is not provided
  const effectiveRenderType: 'mini' | 'full' | 'extension' =
    renderType ?? (isExtension() ? 'extension' : 'mini');

  return SIDEBAR_SECTIONS.filter((section) => section.position === position)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        // Default to ['mini'] if renderType is not specified
        const itemRenderTypes = item.renderType ?? ['mini'];
        return itemRenderTypes.includes(effectiveRenderType);
      }),
    }))
    .filter((section) => section.items.length > 0); // Only return sections with items
};

/**
 * Get all items flattened
 */
export const getAllSidebarItems = () => {
  return SIDEBAR_SECTIONS.flatMap((section) => section.items);
};

/**
 * Get item by id
 */
export const getSidebarItemById = (id: string) => {
  return getAllSidebarItems().find((item) => item.id === id);
};
